import React, { useEffect, useState } from "react";
import ComponentPanel from "../../../components/atoms/ComponentPanel/ComponentPanel";
import store from "../../../state/store";
import withErrorGuard from "../../../HOC/withErrorGuard";
import { httpRequest } from "../../../services/http";
import { gray60, whiteColor } from "../../../common/helpers/colors";
import HelperText from "../../../components/atoms/HelperText";

const BEACON_BASE_URL = "https://www.beacon-network.org/api/";

const BeaconNetwork = () => {
  const fetchBeacons = () => {
    const [beacs, setBeacs] = useState([]);

    const fetchData = () => {
      return fetch("https://www.beacon-network.org/api/beacons")
        .then((response) => response.json())
        .then((data) => setBeacs(data));
    };

    useEffect(() => {
      fetchData();
    }, []);

    return beacs.map((uniqueBeacon) => uniqueBeacon.id);
  };
  const BEACON_IDS = fetchBeacons();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [queried, setQueried] = useState(false);
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [found, setFound] = useState([]);
  const [notFound, setNotFound] = useState([]);
  const [notSupported, setNotSupported] = useState([]);
  const [errored, setErrored] = useState([]);

  const clearResults = () => {
    setFound([]);
    setNotFound([]);
    setNotSupported([]);
    setErrored([]);
    setPendingRequests([]);
  };
  const getBeaconParams = (chr, pos, ref, alt, beaconId) => {
    const chromosome = chr.toUpperCase().replace("CHR", "").replace("M", "MT");
    let allele = alt;
    if (!allele) {
      allele = "D".repeat(ref.length);
    }
    return `${BEACON_BASE_URL}responses/${beaconId}?chrom=${chromosome}&pos=${pos}&allele=${allele}&ref=${
      store.getState().genome.name
    }`;
  };
  const abortPendingRequests = () => {
    pendingRequests.map((controller) => {
      controller.abort();
      return null;
    });
    setPendingRequests([]);
  };
  const supportedGenome = (supportedReferences) => {
    if (!Array.isArray(supportedReferences)) return false;
    const refGenome = store.getState().genome.name.toUpperCase();
    return supportedReferences.indexOf(refGenome) > -1;
  };
  const getOrganizationLogo = (orgName) => {
    const orgs = organizations;
    for (let i = orgs.length - 1; i >= 0; i--) {
      if (orgs[i].name === orgName)
        return `data:image/png;base64,${orgs[i].logo}`;
    }
    return null;
  };
  const getOrganizationUrl = (orgName) => {
    const orgs = organizations;
    for (let i = orgs.length - 1; i >= 0; i--) {
      if (orgs[i].name === orgName) return orgs[i].url;
    }
    return null;
  };
  const listOrganizations = () => {
    const url = `${BEACON_BASE_URL}organizations`;
    httpRequest
      .get(url)
      .then((response) => {
        setOrganizations(response.data);
      })
      .catch((err) => {
        console.error("While listing organizations:", err);
      });
  };

  const queryBeacons = () => {
    clearResults();
    setQueried(true);
    setLoading(true);
    if (!organizations.length) {
      listOrganizations();
      return;
    }
    const v = store.getState().variantResult;
    abortPendingRequests();
    let numResponses = 0;
    const beaconIdsLength = BEACON_IDS.length;
    BEACON_IDS.forEach((beaconId) => {
      const url = getBeaconParams(v.chromosome, v.pos, v.ref, v.alt, beaconId);
      const controller = new AbortController();
      pendingRequests.push(controller);
      httpRequest
        .get(url, { signal: controller.signal })
        .then((res) => {
          const response = res.data;
          if (response.response === true) {
            found.push(response.beacon);
            setFound(found);
          } else if (response.response === false) {
            notFound.push(response.beacon);
            setNotFound(notFound);
          } else if (
            response.response === null &&
            !supportedGenome(response.supportedReferences)
          ) {
            notSupported.push(response.beacon);
            setNotSupported(notSupported);
          } else {
            errored.push(response.beacon);
            setErrored(errored);
          }
          numResponses += 1;
          if (numResponses === beaconIdsLength) {
            setLoading(false);
          }
        })
        .catch((err) => {
          console.error("While querying beacon:", err);
          const failedUrl = { id: beaconId, url };
          errored.push(failedUrl);
          setErrored(errored);
          numResponses += 1;
          if (numResponses === beaconIdsLength) {
            setLoading(false);
          }
        });
    });
  };

  useEffect(() => {
    if (organizations.length > 0) {
      queryBeacons();
    }
  }, [organizations]);

  const foundWidth = (found.length * 100) / BEACON_IDS.length;
  const notFoundWidth = (notFound.length * 100) / BEACON_IDS.length;
  const notSupportedWidth = (notSupported.length * 100) / BEACON_IDS.length;
  const erroredWidth = (errored.length * 100) / BEACON_IDS.length;
  return (
    <div className="varsome-result">
      <div className="varsome-container varsome-container__basic">
        <ComponentPanel
          title="Beacon Network"
          helperPopup={<HelperText fieldDescriptionKey="beaconNetwork" />}
          content={
            <div className="varsome-result">
              <div className="varsome-container varsome-container__basic">
                <div>
                  <div className="varsome-inner-container">
                    <div className="varsome-horiz-flex">
                      {!queried && (
                        <div className="varsome-horiz-list-item">
                          <button
                            type="button"
                            onClick={() => queryBeacons()}
                            className="btn btn-success tw-text-white"
                          >
                            Query Beacon Network
                          </button>
                        </div>
                      )}
                    </div>
                    {queried && (
                      <div>
                        <div
                          className="varsome-clearfix tw-w-full"
                          style={{ border: "1px solid #999" }}
                        >
                          <div
                            className="tw-float-left tw-text-center"
                            style={{
                              height: "30px",
                              width: `${foundWidth}%`,
                              backgroundColor: "green",
                            }}
                          >
                            <small
                              className="tw-font-bold"
                              style={{ color: whiteColor }}
                            >
                              {found.length}
                            </small>
                          </div>
                          <div
                            className="tw-float-left tw-text-center"
                            style={{
                              height: "30px",
                              width: `${notFoundWidth}%`,
                              backgroundColor: "grey",
                            }}
                          >
                            <small
                              className="tw-font-bold"
                              style={{ color: whiteColor }}
                            >
                              {notFound.length}
                            </small>
                          </div>
                          <div
                            className="tw-float-left tw-text-center"
                            style={{
                              height: "30px",
                              width: `${notSupportedWidth}%`,
                              backgroundColor: "magenta",
                            }}
                          >
                            <small
                              className="tw-font-bold"
                              style={{ color: whiteColor }}
                            >
                              {notSupported.length}
                            </small>
                          </div>
                          <div
                            className="tw-float-left tw-text-center"
                            style={{
                              height: "30px",
                              width: `${erroredWidth}%`,
                              backgroundColor: "red",
                            }}
                          >
                            <small
                              className="tw-font-bold"
                              style={{ color: whiteColor }}
                            >
                              {errored.length}
                            </small>
                          </div>
                        </div>
                        <div className="varsome-horiz-flex">
                          <div className="varsome-horiz-list-item">
                            <span
                              className="tw-inline-block tw-h-4 tw-w-4"
                              style={{
                                backgroundColor: "green",
                              }}
                            />
                            {"  Found"}
                            <span
                              className="tw-ml-4 tw-inline-block tw-h-4 tw-w-4"
                              style={{
                                backgroundColor: "grey",
                              }}
                            />
                            {" Not found"}
                            <span
                              className="tw-ml-4 tw-inline-block tw-w-4 tw-h-4"
                              style={{
                                backgroundColor: "magenta",
                              }}
                            />
                            {" Not supported by beacon"}

                            <span
                              className="tw-ml-4 tw-w-4 tw-h-4 tw-inline-block"
                              style={{
                                backgroundColor: "red",
                              }}
                            />
                            {" Error"}
                          </div>
                        </div>
                        <table className="varsome-table">
                          <tbody>
                            {found.map((beacon, i) => {
                              return (
                                <tr key={i}>
                                  <td style={{ width: "70%" }}>
                                    <img
                                      className="tw-align-top tw-mr-4"
                                      width="40"
                                      height="40"
                                      src={getOrganizationLogo(
                                        beacon.organization
                                      )}
                                    />
                                    <span className="tw-inline-block">
                                      {beacon.name}
                                      {beacon.aggregator && (
                                        <i
                                          className="fa fa-sitemap tw-ml-2"
                                          data-tooltip="Agreggates from multiple beacons"
                                        />
                                      )}
                                      <br />
                                      <a
                                        target="_blank"
                                        href={getOrganizationUrl(
                                          beacon.organization
                                        )}
                                      >
                                        {beacon.organization}
                                      </a>
                                    </span>
                                  </td>
                                  <td
                                    className="tw-text-right"
                                    style={{
                                      width: "30%",
                                    }}
                                  >
                                    <i
                                      className="fa fa-check"
                                      style={{ color: "green" }}
                                    />
                                    {"  Found"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {loading && (
                          <div className="varsome-text-center">
                            <i
                              className="fa fa-circle-o-notch fa-2x fa-spin"
                              style={{ color: gray60 }}
                            />
                          </div>
                        )}
                        {!loading && found.length === 0 && (
                          <div className="varsome-text-center">
                            <span style={{ color: gray60 }}>
                              Not found on any beacon
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default withErrorGuard(BeaconNetwork, "BeaconNetwork", "Beacon Network");
